// src/components/publishing/available-servers-strip.tsx
//
// The destinations strip on Brand Publisher.
//
// Source: handoff-advanced §2.1 and the "destinations strip" region of
// `Brand Publisher - Polished.dc.html`.
//
// ── What changed, and why it is not a behaviour change ─────────────────────
//
// It used to render only the destinations that ARE connected, so a page with
// nothing connected showed one empty state and a page with one connected
// showed one pill — and in neither case could you tell what the other two
// were, or that they existed. Connection state gates publishing, and a state
// that gates something has to be legible before you hit the gate.
//
// So it renders all three, every time, and says which are connected. The prop
// is the same prop; what changed is that the page hands it the whole list
// rather than a pre-filtered one, and this component does the filtering it was
// always doing — just visibly.
//
// The controls are still elsewhere: connect and disconnect live on the
// Integrations page, and this strip still only links there.

import { Settings2 } from "lucide-react";
import {
  DESTINATIONS, Destination, Integration, daysUntilExpiry,
} from "@/app/services/publishing/publishing-api";
import { Button, Spinner } from "../kit";
import {
  DEST_META, DestinationDot, destinationLabel,
} from "@/app/components/shared/advanced-vocabulary";

interface AvailableServersStripProps {
  integrations: Integration[];
  loading?: boolean;
  onManageClick?: () => void;
}

export function AvailableServersStrip({
  integrations, loading, onManageClick,
}: AvailableServersStripProps) {
  if (loading) {
    return (
      <div className="upm-pub__strip">
        <Spinner label="Loading destinations" />
      </div>
    );
  }

  const byId = new Map(integrations.map((i) => [i.destination, i]));

  return (
    <div className="upm-pub__strip">
      <span className="upm-adv-eyebrow">Destinations</span>

      {DESTINATIONS.map((d: Destination) => {
        const integration = byId.get(d);
        const connected = integration?.status === "active";
        // An expiring credential is neither connected-and-fine nor
        // disconnected. It is the one case where the pill has something to add
        // beyond the two words, so it says how long is left.
        const days = daysUntilExpiry(integration?.expires_at);
        const expiring = connected && typeof days === "number" && days <= 14;

        return (
          <span
            key={d}
            className="upm-pub__destpill"
            data-connected={connected}
            title={DEST_META[d]?.blurb}
          >
            <DestinationDot id={d} />
            {destinationLabel(d)}
            {connected ? (
              expiring ? (
                <span className="upm-pub__destpill-state">
                  Expires in {days} {days === 1 ? "day" : "days"}
                </span>
              ) : null
            ) : (
              <span className="upm-pub__destpill-state">Not connected</span>
            )}
          </span>
        );
      })}

      {onManageClick && (
        <span className="upm-pub__stripend">
          <Button variant="ghost" icon={Settings2} onClick={onManageClick}>
            Manage destinations
          </Button>
        </span>
      )}
    </div>
  );
}
